'use client';
import React, { useEffect, useRef } from 'react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

export default function TermsPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef?.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <main className="bg-canopy min-h-screen" ref={sectionRef}>
      <Header />

      <div className="pt-32 pb-20 px-6 md:px-10 blueprint-grid">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-px bg-chartreuse opacity-60" />
              <span className="annotation">SYARAT & KETENTUAN</span>
            </div>
            <h1 className="font-mono font-bold text-4xl md:text-5xl text-text-primary mb-4 leading-tight">
              Syarat &<br />
              <span className="text-chartreuse">Ketentuan.</span>
            </h1>
            <p className="text-text-muted text-sm">
              Terakhir diperbarui: 2025 · Agro Teknologi Nusantara
            </p>
          </div>

          <div className="space-y-10">
            {/* Penggunaan situs web */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">1. Penggunaan Situs Web</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Website Agro Teknologi Nusantara hanya boleh digunakan untuk tujuan
                yang sah. Anda setuju untuk tidak menggunakan situs ini untuk tujuan ilegal atau
                yang dapat merusak integritas layanan kami. Kami berhak membatasi atau menghentikan
                akses siapa pun yang melanggar ketentuan ini.
              </p>
            </section>

            {/* Produk dan layanan */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">2. Produk dan Layanan</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Semua produk dan layanan Agro Teknologi Nusantara dimaksudkan untuk digunakan sesuai spesifikasi
                yang diberikan. Kami berhak mengubah, memperbarui, atau menghentikan layanan
                atau produk kapan saja tanpa pemberitahuan sebelumnya. Informasi di website
                dapat berubah tanpa jaminan akurasi terkini.
              </p>
            </section>

            {/* Pembayaran */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">3. Pembayaran</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Pembayaran untuk layanan IoT dan produk pertanian dilakukan melalui metode yang
                disepakati bersama (transfer bank, e-wallet, atau metode lain yang tersedia).
                Semua harga yang tercantum belum termasuk pajak yang berlaku kecuali stated
                otherwise. Tagihan harus dibayar sesuai jadwal yang ditetapkan.
              </p>
            </section>

            {/* Penggunaan data */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">4. Penggunaan Data</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Dengan menggunakan layanan IoT Agro Teknologi Nusantara, Anda memberikan izin untuk mengumpulkan,
                memproses, dan menganalisis data dari perangkat Anda sesuai dengan <a href="/kebijakan-privasi" className="text-chartreuse hover:underline">Kebijakan Privasi </a>
                kami. Data digunakan untuk monitoring, forecasting, penelitian, dan pengembangan
                sistem pertanian cerdas.
              </p>
            </section>

            {/* Kepatuhan pengguna */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">5. Kepatuhan Pengguna</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Pengguna layanan Agro Teknologi Nusantara setuju untuk menggunakan produk dan layanan sesuai
                panduan teknis yang diberikan. Kerusakan akibat ketidakpatuhan terhadap panduan
                instalasi atau penggunaan tidak menjadi tanggung jawab Agro Teknologi Nusantara.
              </p>
            </section>

            {/* Pembatasan tanggung jawab */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">6. Pembatasan Tanggung Jawab</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Sepanjang diizinkan oleh hukum yang berlaku, Agro Teknologi Nusantara tidak bertanggung jawab
                atas kerusakan langsung, tidak langsung, insidental, atau konsekuensial yang
                timbul dari penggunaan layanan atau produk kami. Ini termasuk kehilangan hasil
                panen, kerugian finansial, atau gangguan operasional.
              </p>
            </section>

            {/* Perubahan syarat */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">7. Perubahan Syarat</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Kami dapat mengubah Syarat & Ketentuan ini kapan saja. Perubahan akan berlaku
                segera setelah dipublikasikan di website. Penggunaan berkelanjutan terhadap
                layanan kami setelah perubahan berarti Anda menerima syarat yang baru.
              </p>
            </section>

            {/* Hukum yang berlaku */}
            <section className="reveal">
              <h2 className="font-mono font-bold text-xl text-chartreuse mb-4">8. Hukum yang Berlaku</h2>
              <p className="text-text-muted text-sm leading-relaxed">
                Syarat & Ketentuan ini diatur oleh hukum Republik Indonesia. Setiap sengketa
                yang timbul akan diselesaikan melalui negosiasi terlebih dahulu, dan jika tidak
                mencapai kesepakatan, akan diselesaikan di pengadilan yang berwenang.
              </p>
            </section>
          </div>

          {/* CTA */}
          <div className="reveal mt-16 border border-chartreuse/20 rounded-sm p-8 bg-pond/30 text-center">
            <p className="text-text-muted text-sm mb-4">
              Pertanyaan tentang syarat dan ketentuan?
            </p>
            <a
              href="/quote"
              className="btn-chartreuse px-6 py-3 rounded-sm text-xs uppercase tracking-widest inline-block"
            >
              Hubungi Kami →
            </a>
          </div>
        </div>
      </div>

      <Footer />
    </main>
  );
}
